// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import {
  Modal,
  Pill,
  Sans14,
  Serif24,
  spacing,
} from "@recidiviz/design-system";
import { observer } from "mobx-react-lite";
import { rem, rgba } from "polished";
import React from "react";
import ReactSelect, {
  ClearIndicatorProps,
  components,
  GroupBase,
  IndicatorsContainerProps,
  MenuListProps,
  MultiValueProps,
  MultiValueRemoveProps,
  OptionProps,
  SelectComponentsConfig,
  StylesConfig,
} from "react-select";
import styled from "styled-components";

import { Button, Icon, IconSVG, palette } from "~design-system";

import { useRootStore } from "../../components/StoreProvider";
import useIsMobile from "../../hooks/useIsMobile";
import { Searchable, SearchableGroup, SearchIcon } from "../models/types";
import {
  createMenuListWithScrollShadow,
  searchBarContainerStyles,
  searchBarControlStyles,
  searchBarGroupHeadingStyles,
  searchBarGroupStyles,
  searchBarMenuStyles,
  searchBarOptionHoverStyle,
  searchBarOptionTextColor,
  searchBarPlaceholderStyles,
} from "../WorkflowsSearchBar/WorkflowsSearchBar.styles";

// This is a query limitation imposed by Firestore
const SELECTED_SEARCH_LIMIT = 30;

const ValuePill = styled(Pill).attrs({ color: palette.slate20, filled: false })`
  align-self: flex-start;
  border-radius: ${rem(8)};
  font-size: inherit;
  height: ${rem(32)};
`;

const DisabledMessage = styled.div`
  color: ${palette.signal.notification};
  /* non-standard padding value to match library styles */
  padding: ${rem(spacing.sm)} ${rem(spacing.md)};
`;

export type SelectOption = { label: string; value: string; icon?: SearchIcon };

type SelectGroupedOptions = { label: string; options: SelectOption[] };

const buildSelectOptionsFromSearchableGroup = (
  searchableGroup: SearchableGroup[],
): SelectGroupedOptions[] | SelectOption[] => {
  if (searchableGroup.length === 1) {
    return buildSelectOptionsFromSearchables(searchableGroup[0].searchables);
  }

  return searchableGroup.map((group) => ({
    label: group.groupLabel,
    options: buildSelectOptionsFromSearchables(group.searchables),
  }));
};

const buildSelectOptionsFromSearchables = (
  searchables: Searchable[],
): SelectOption[] => {
  return searchables.map((searchable) => ({
    label: searchable.searchLabel,
    value: searchable.searchId,
    icon: searchable.icon,
  }));
};

const DistrictIndicatorsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: end;
  row-gap: ${rem(spacing.xs)};
`;

const DistrictIndicator = observer(function DistrictIndicator() {
  const {
    workflowsStore: { districtsFilteredBy },
  } = useRootStore();

  if (!districtsFilteredBy) return null;

  return (
    <DistrictIndicatorsWrapper>
      {districtsFilteredBy.map((district) => (
        <ValuePill key={district}>
          {district.match(/^\d/) ? `D${district}` : district}&nbsp;
          <Icon kind="Place" size={12} color={palette.slate60} />
        </ValuePill>
      ))}
    </DistrictIndicatorsWrapper>
  );
});

const IndicatorsWrapper = styled.div<{
  isMobile: boolean;
  hasDistricts: boolean;
}>`
  display: flex;
  flex-basis: ${({ isMobile, hasDistricts }) =>
    isMobile || !hasDistricts ? "initial" : "30%"};
  flex-wrap: wrap;
  align-items: center;
  justify-content: end;
  row-gap: ${rem(spacing.sm)};
  margin-top: ${({ isMobile }) => (isMobile ? rem(spacing.md) : 0)};
`;

const Indicators = (isMobile: boolean) =>
  observer(function IndicatorsContainer({
    children,
  }: IndicatorsContainerProps<SelectOption, true>) {
    const {
      workflowsStore: { districtsFilteredBy },
    } = useRootStore();

    return (
      <IndicatorsWrapper
        isMobile={isMobile}
        hasDistricts={Boolean(districtsFilteredBy)}
      >
        {children}
        <DistrictIndicator />
      </IndicatorsWrapper>
    );
  });

const Option = ({ children, ...props }: OptionProps<SelectOption, true>) => {
  return (
    <components.Option className="fs-exclude" {...props}>
      {children}
    </components.Option>
  );
};

const IconContainer = styled.div`
  display: flex;
  justify-content: center;

  padding: ${rem(spacing.xs)};

  // TODO(#8709): Add color to design system or change to design system color
  background-color: rgba(226, 244, 255, 1);
  border-radius: 50%;
`;

const SearchIconElement = styled.i`
  color: ${palette.logoBlue};
`;

const SearchableIcon = ({ icon }: { icon?: SearchIcon }) => {
  switch (icon) {
    case "flag":
      return (
        <IconContainer>
          <SearchIconElement className="fa fa-flag" />
        </IconContainer>
      );
    default:
      return null;
  }
};

const MultiValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.xs)};
`;

export const MultiValue = ({
  children,
  ...props
}: MultiValueProps<SelectOption, true>) => {
  const {
    data,
    data: { icon },
    selectProps,
  } = props;

  const handleRemove = () => {
    // Filter out the item to remove
    const newValues = Array.isArray(selectProps?.value)
      ? selectProps.value.filter((item) => item.value !== data.value)
      : [];
    // Update the selected values
    selectProps.onChange(newValues, {
      action: "remove-value",
      removedValue: data,
    });
  };

  return (
    <components.MultiValue className="fs-exclude" {...props}>
      <MultiValueContainer onTouchStart={handleRemove}>
        <SearchableIcon icon={icon} />
        {children}
      </MultiValueContainer>
    </components.MultiValue>
  );
};

export const ValueRemover = (props: MultiValueRemoveProps<SelectOption>) => {
  return (
    <components.MultiValueRemove {...props}>
      <Icon kind="CloseOutlined" size={14} />
    </components.MultiValueRemove>
  );
};

const ClearAll = (props: ClearIndicatorProps<SelectOption, true>) => {
  return (
    <components.ClearIndicator {...props}>Clear all</components.ClearIndicator>
  );
};

export type MenuDisabledState =
  | { isDisabled: true; disabledMessage: string }
  | { isDisabled: false; disabledMessage?: never };

export const MenuListWithShadow = (
  entriesNumber: number,
  disabledState: MenuDisabledState,
) => {
  const BaseMenuList = createMenuListWithScrollShadow<SelectOption, true>(
    entriesNumber,
  );

  return function MenuList(props: MenuListProps<SelectOption, true>) {
    return (
      <BaseMenuList {...props}>
        {disabledState.isDisabled && (
          <DisabledMessage>{disabledState.disabledMessage}</DisabledMessage>
        )}
        {props.children}
      </BaseMenuList>
    );
  };
};

const CaseloadSelectContainer = styled(Sans14)`
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;

  color: ${palette.slate85};
  margin-bottom: ${rem(spacing.lg)};
`;

const CaseloadSelectMobileButton = styled(Button).attrs({ kind: "link" })`
  color: ${palette.signal.links};
  padding: 0 0.5rem;
`;

const MobileSearchModal = styled(Modal)`
  .ReactModal__Content {
    font-family: "Public Sans", sans-serif;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.2;
    letter-spacing: -0.01em;
    max-width: unset;
    max-height: unset;
    width: 100%;
    height: 100%;
    padding: 1rem;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`;

const ModalButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ModalButton = styled(Button).attrs({ kind: "link" })`
  color: ${palette.signal.links};
  text-decoration: none !important;
  padding: ${rem(spacing.sm)} 0;
`;

export const ModalCloseButton = styled(ModalButton).attrs({ kind: "link" })``;

export const ModalHeader = styled(Serif24)`
  color: ${palette.pine2};
  padding-top: ${rem(spacing.md)};
`;

type CaseloadSelectProps = {
  hideIndicators?: boolean;
};

export const caseloadSelectStyles = (
  isMobile: any,
  hideIndicators: boolean,
): Partial<StylesConfig<SelectOption, true, GroupBase<SelectOption>>> => ({
  placeholder: (base) => searchBarPlaceholderStyles(base),
  clearIndicator: (base) => ({
    ...base,
    color: palette.slate85,
    cursor: "pointer",
    fontSize: rem(14),
    margin: `0 ${rem(spacing.sm)}`,
    padding: 0,
    textTransform: "capitalize",
    "&:hover": {
      color: palette.slate,
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    display: hideIndicators ? "none" : "inherit",
  }),
  multiValue: (base) => ({
    ...base,
    alignItems: "center",
    background: "transparent",
    border: `1px solid ${palette.slate20}`,
    borderRadius: rem(8),
    height: rem(30),
    margin: 0,
    padding: rem(spacing.sm),
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: palette.slate85,
    fontSize: isMobile ? rem(12) : rem(14),
    lineHeight: rem(16),
    padding: 0,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: rgba(palette.slate, 0.4),
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "transparent",
      color: palette.slate60,
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: 0,
    gap: rem(spacing.sm),
    color: palette.slate60,
  }),
  container: (base) => ({
    ...searchBarContainerStyles(base),

    ...(isMobile
      ? {
          margin: "0 -1rem",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }
      : {}),
  }),
  menuList: (base) => ({
    ...base,
    padding: `${rem(spacing.sm)} 0`,
    maxHeight: isMobile ? "unset" : rem(300),
  }),
  control: (base, state) => ({
    ...searchBarControlStyles(base),
    borderStyle: "solid",
    borderColor: `${palette.slate30} !important`,
    borderWidth: "1px",
    borderBottomWidth: state.menuIsOpen ? "0" : "1px",
    borderRadius: state.menuIsOpen ? "4px 4px 0 0" : rem(4),
    background: "#fff",
    padding: rem(5),
    margin: 0,
  }),
  menu: (base) => ({
    ...searchBarMenuStyles(base, isMobile),
    margin: 0,
    borderTop: `1px solid ${palette.slate20}`,
    borderRadius: "0 0 4px 4px",

    // By default the menu is absolutely positioned, but on mobile devices
    // we want it to take up the whole screen
    ...(isMobile
      ? {
          position: "unset",
          top: "unset",
          flex: "1",
          overflowY: "auto",
        }
      : {}),
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: "none",
    color: state.isDisabled ? palette.slate20 : searchBarOptionTextColor,
    pointerEvents: state.isDisabled ? "none" : "initial",
    padding: isMobile
      ? `${rem(10)} ${rem(spacing.xl)}`
      : `${rem(spacing.sm)} ${rem(spacing.md)}`,
    ...searchBarOptionHoverStyle,
  }),
  group: (base) => searchBarGroupStyles(base),
  groupHeading: (base) => ({
    ...searchBarGroupHeadingStyles(base),
    padding: `${rem(2)} ${rem(spacing.md)}`,
  }),
});

export const CaseloadSelect = observer(function CaseloadSelect({
  hideIndicators = false,
}: CaseloadSelectProps) {
  const { workflowsStore, analyticsStore } = useRootStore();
  const { isMobile } = useIsMobile(true);
  const {
    searchStore: {
      selectedSearchables,
      selectedSearchIds,
      searchType,
      availableSearchables,
      updateSelectedSearch,
      isTypesenseSearchEnabled,
      caseloadSearchManager,
      facilityUnitSearchIds,
    },
  } = workflowsStore;

  const searchableGroups = availableSearchables;

  const customComponents: SelectComponentsConfig<
    SelectOption,
    true,
    GroupBase<SelectOption>
  > = {
    ClearIndicator: ClearAll,
    DropdownIndicator: null,
    IndicatorsContainer: Indicators(isMobile),
    MultiValueRemove: ValueRemover,
    Option,
    MultiValue,
  };

  const disableAdditionalSelections =
    selectedSearchables.length >= SELECTED_SEARCH_LIMIT;

  // TODO(OBT-49983): Temporary. A handful of large facility units is enough
  // to exhaust Firestore's request quota, so tenants with a FACILITY_UNIT
  // search config can set `maxFacilityUnitSearchIds` (see
  // `WorkflowsSystemConfig`) to cap how many can be selected at once. States
  // that don't set it are unaffected. Checked per selected id, not via
  // `searchType`, so this also applies on pages like the homepage where
  // searchType is "ALL" but some of the selected ids are still facility
  // units. Remove once the Typesense-backed search refactor ships.
  const maxFacilityUnitSearchIds =
    workflowsStore.systemConfigFor("INCARCERATION").maxFacilityUnitSearchIds;
  const selectedFacilityUnitIds = selectedSearchIds.filter((id) =>
    facilityUnitSearchIds.has(id),
  );
  const isFacilityUnitSelectionCapped =
    maxFacilityUnitSearchIds !== undefined &&
    selectedFacilityUnitIds.length >= maxFacilityUnitSearchIds;

  const getMenuDisabledState = (): MenuDisabledState => {
    if (disableAdditionalSelections) {
      return {
        isDisabled: true,
        disabledMessage: `Cannot select more than ${SELECTED_SEARCH_LIMIT} items.`,
      };
    }
    if (isFacilityUnitSelectionCapped) {
      return {
        isDisabled: true,
        disabledMessage: "Cannot select any more units right now.",
      };
    }
    return { isDisabled: false };
  };
  const menuDisabledState = getMenuDisabledState();

  const numAvailableSearchables = searchableGroups.reduce(
    (acc, group) => acc + group.searchables.length,
    0,
  );

  customComponents.MenuList = MenuListWithShadow(
    numAvailableSearchables,
    menuDisabledState,
  );

  const defaultOptions = {
    classNamePrefix: "CaseloadSelect",
    className: "CaseloadSelect",
    components: customComponents,
    isMulti: true,
    isOptionDisabled: (option: SelectOption) => {
      if (disableAdditionalSelections) return true;
      if (!isFacilityUnitSelectionCapped) return false;
      return (
        facilityUnitSearchIds.has(option.value) &&
        !selectedFacilityUnitIds.includes(option.value)
      );
    },
    onChange: (newValue: any) => {
      updateSelectedSearch(newValue.map((item: SelectOption) => item.value));
      analyticsStore.trackCaseloadSearch({
        searchCount: newValue.length,
        isDefault: false,
        searchType,
      });
    },
    // When Typesense is on, drive the server-side typeahead on every input
    // change (the store owns the debounce + fetch) and let react-select skip
    // its built-in client-side filter since the server already returned the
    // matching set for this input.
    ...(isTypesenseSearchEnabled
      ? {
          onInputChange: (newValue: string) => {
            caseloadSearchManager.handleSearchInput(newValue);
            return newValue;
          },
          filterOption: () => true,
        }
      : {}),
    options: buildSelectOptionsFromSearchableGroup(searchableGroups),
    placeholder: isTypesenseSearchEnabled
      ? "Start typing …"
      : "Search for one or more caseloads …",
    styles: caseloadSelectStyles(isMobile, hideIndicators),
    value: buildSelectOptionsFromSearchables(selectedSearchables),
    // use of satisfies narrows isMulti from boolean to true,
    // which the custom components defined here will require
  } satisfies { isMulti: true; [key: string]: unknown };

  const [modalIsOpen, setModalIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isMobile) setModalIsOpen(false);
  }, [isMobile]);

  if (isMobile) {
    return (
      <CaseloadSelectContainer>
        Caseloads:
        <CaseloadSelectMobileButton onClick={() => setModalIsOpen(true)}>
          {selectedSearchIds.length > 0
            ? `${selectedSearchIds.length} selected`
            : "Click to select caseloads"}
          &nbsp;
          <Icon kind={IconSVG.DownChevron} width={8} />
        </CaseloadSelectMobileButton>
        <MobileSearchModal
          isOpen={modalIsOpen}
          onRequestClose={() => setModalIsOpen(false)}
        >
          <ModalButtonRow>
            <ModalCloseButton onClick={() => setModalIsOpen(false)}>
              Back
            </ModalCloseButton>
            <ModalButton onClick={() => updateSelectedSearch([])}>
              Clear
            </ModalButton>
          </ModalButtonRow>
          <ModalHeader>Search</ModalHeader>
          <ReactSelect menuIsOpen isClearable={false} {...defaultOptions} />
        </MobileSearchModal>
      </CaseloadSelectContainer>
    );
  }

  return (
    <CaseloadSelectContainer>
      <ReactSelect {...defaultOptions} />
    </CaseloadSelectContainer>
  );
});
